<template>
  <div class="content">
    <div class="reset-wrapper">
      <div class="reset">
        <PublicBrandHeader />
        <h3>Confirm receipt</h3>

        <template v-if="state === 'confirmed'">
          <p class="reset__introduction confirm-result">Thank you. Your confirmation has been recorded.</p>
        </template>

        <template v-else-if="state === 'invalid'">
          <p class="reset__introduction confirm-result">
            This link is no longer valid. It may already have been used, or it may have expired.
          </p>
        </template>

        <template v-else>
          <p class="reset__introduction">
            Please confirm that you have received the notice this link came with.
          </p>
          <div class="button-group">
            <button
              type="button"
              class="btn btn--login"
              id="confirmreceiptbtn"
              :disabled="busy || !token"
              @click="confirm"
            >
              {{ busy ? 'Confirming...' : 'Confirm receipt' }}
            </button>
          </div>
        </template>

        <div v-if="errorText" class="form-error-summary" role="alert">{{ errorText }}</div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Where a notice's "Confirm receipt" link lands (main #283's
 * notification_confirm.php; NT-06).
 *
 * Main confirmed on the GET itself, so every mail and SMS link scanner that
 * fetched the link (Safe Links, Proofpoint, a messaging app's preview)
 * "confirmed" it before the person had seen it. This page confirms nothing
 * on load: only the button posts the token. It needs no sign-in — the token is
 * the credential — and works the same whether or not someone is signed in on
 * this browser.
 *
 * An unknown, already-used and expired link get the same answer, as main's
 * page gave them, so the page cannot be used to tell a live link from a dead
 * one. A failure to reach the server is said as such, and the button stays.
 */
import PublicBrandHeader from '@/components/public-layouts/PublicBrandHeader.vue';
import { confirmNotification } from '@/api2/notifications';

export default {
  name: 'notification-confirm',
  components: {
    PublicBrandHeader,
  },
  props: {
    token: { type: String, default: '' },
  },
  data() {
    return {
      state: 'ready',
      busy: false,
      errorText: '',
    };
  },
  created() {
    if (!this.token) this.state = 'invalid';
  },
  methods: {
    async confirm() {
      if (this.busy || !this.token) return;

      this.busy = true;
      this.errorText = '';

      try {
        this.state = (await confirmNotification(this.token)) ? 'confirmed' : 'invalid';
      } catch (err) {
        this.errorText = err && err.status === 429
          ? 'Too many attempts from this connection. Please wait a minute and try again.'
          : 'We could not record your confirmation right now. Please try again shortly.';
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../stylesheets/mixins/alignment";
$color-base: #0060ff;
.btn--login {
  color: #fff;
  background-color: $color-base;
  border-radius: 0.375em;

  &:hover {
    background-color: lighten($color-base, 5%);
  }
}
.content {
  width: 100%;
  height: auto;
  background: url("../../assets/images/bg.jpg") no-repeat center fixed;
  background-size: cover;
  position: relative;
}
.reset-wrapper {
  height: 100vh;
  width: 100%;
  position: relative;
}
.reset {
  width: 60% !important;
  background-color: #fff;
  padding: 2rem;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12),
    0 1px 5px 0 rgba(0, 0, 0, 0.2);

  @include absoluteCenter(380px);

  > h3 {
    text-align: center;
  }
}
.reset__introduction {
  color: darken(gray, 10%);
  font-size: 14px;
  text-align: center;
}
.button-group {
  margin-top: 1.5rem;
  text-align: center;
}
.form-error-summary {
  margin: 0.75rem 0 0;
  padding: 0.9rem 1.15rem;
  background: #fdecea;
  border: 1px solid #f3b4b0;
  border-radius: 6px;
  color: #9b2c2c;
}
@media only screen and (max-width: 768px) {
  .reset {
    width: 90% !important;
  }
}
</style>
